import React from 'react';

import type { MapMove, MapPing } from '@features/mapVisualization/types';
import type { Moves_Bool_Exp } from '@gql/base-types';
import { useGetDashboardMovesMapQuery } from '../queries/getDashboardMovesMap.query.generated';
import { useGetDriverMapPingsQuery } from '../queries/getDriverMapPings.query.generated';
import {
  MAP_MAX_MOVES,
  MAP_MAX_DRIVERS,
  buildMoveColorMap,
} from '@features/mapVisualization/constants';

import { useRooftop, useUser } from '@store';


// DRIVER SHAPE -------------------------------------------------------------- //

export interface MapDriver {
  id: number;
  status: string;
  user?: {
    id: number;
    display_name?: string;
    avatar_url?: string;
    phone?: string;
    email?: string;
  };
  gpslocations?: Array<{
    id: string;
    location: { type: string; coordinates: number[] };
    time: string;
    speed?: number | null;
    heading?: number | null;
    is_moving?: boolean | null;
    battery_level?: number | null;
    battery_is_charging?: boolean | null;
  }>;
  moveCount: number;
}

// INPUT --------------------------------------------------------------------- //

export interface UseDriverMapDataInput {
  /** Inclusive window bounds as ISO strings; the host tab owns the date state. */
  start: string;
  end: string;
  additionalFilters: Moves_Bool_Exp[];
  /** Not an access gate. Off skips both queries, stops polling, and clears the driver selection. */
  enabled: boolean;
}

// RETURN TYPE --------------------------------------------------------------- //

export interface UseDriverMapDataReturn {
  moves: MapMove[];
  drivers: MapDriver[];
  moveColorMap: Record<number, string>;
  pingsByMoveId: Record<number, MapPing[]>;
  unassignedPings: MapPing[];
  selectedDriverId: number | null;
  handleSelectDriver: (driverId: number | null) => void;
  highlightedMoveId: number | null;
  setHighlightedMoveId: (moveId: number | null) => void;
  driverMoves: MapMove[];
  loading: boolean;
  pingsLoading: boolean;
  movesTruncated: boolean;
  driversTruncated: boolean;
  totalMoveCount: number;
  refetch: () => void;
}

// HOOK ---------------------------------------------------------------------- //

export function useDriverMapData({ start, end, additionalFilters, enabled }: UseDriverMapDataInput): UseDriverMapDataReturn {
  const rooftop = useRooftop();
  const user = useUser();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // Fetch moves for map (higher limit to fill map, then cap client-side)
  const { data: movesData, loading: movesLoading, refetch } = useGetDashboardMovesMapQuery({
    variables: {
      start,
      end,
      customerId,
      additionalFilters,
      limit: MAP_MAX_MOVES,
    },
    skip: !customerId || !enabled,
    fetchPolicy: 'cache-and-network',
    pollInterval: enabled ? 10_000 : 0,
  });

  // Selected driver state
  const [selectedDriverId, setSelectedDriverId] = React.useState<number | null>(null);
  const [highlightedMoveId, setHighlightedMoveId] = React.useState<number | null>(null);

  // Pings for selected driver — polls every 10s while a driver is selected; shares the window start.
  const { data: pingsData, loading: pingsLoading } = useGetDriverMapPingsQuery({
    variables: { driverId: selectedDriverId, todayStart: start },
    skip: !selectedDriverId || !enabled,
    fetchPolicy: 'cache-and-network',
    pollInterval: selectedDriverId && enabled ? 10_000 : 0,
  });

  // Clear selection when leaving map mode
  React.useEffect(() => {
    if (!enabled) {
      setSelectedDriverId(null);
      setHighlightedMoveId(null);
    }
  }, [enabled]);

  // Parse moves
  const allMoves: MapMove[] = movesData?.moves || [];

  // Ref to latest moves so handleSelectDriver stays stable (avoids unstable deps)
  const allMovesRef = React.useRef(allMoves);
  allMovesRef.current = allMoves;

  const handleSelectDriver = React.useCallback(
    (driverId: number | null) => {
      setSelectedDriverId(driverId);
      if (driverId) {
        // Auto-select the actively-worked move for this driver
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const activeMove = allMovesRef.current.find((m: any) =>
          m.driver_id === driverId &&
          m.status != null &&
          m.status !== 'dispatched' &&
          m.status !== 'delivery successful' &&
          !m.cancel_status
        );
        setHighlightedMoveId(activeMove?.id ?? null);
      } else {
        setHighlightedMoveId(null);
      }
    },
    []
  );
  const totalMoveCount = allMoves.length;

  // Apply caps — moves sorted by pickup_time ASC (already from query)
  const cappedMoves = React.useMemo(() => allMoves.slice(0, MAP_MAX_MOVES), [allMoves]);
  const movesTruncated = totalMoveCount > MAP_MAX_MOVES;

  // Derive unique drivers from capped moves, cap at MAP_MAX_DRIVERS
  const { drivers, driversTruncated } = React.useMemo(() => {
    const driverMap = new Map<number, MapDriver>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cappedMoves.forEach((move: any) => {
      const driver = move.driver;
      if (!driver?.id) return;

      const existing = driverMap.get(driver.id);
      if (existing) {
        existing.moveCount++;
      } else {
        driverMap.set(driver.id, {
          id: driver.id,
          status: driver.status,
          user: driver.user,
          gpslocations: driver.gpslocations,
          moveCount: 1,
        });
      }
    });

    // Sort by most-recent-ping-first
    const sorted = Array.from(driverMap.values()).sort((a, b) => {
      const aTime = a.gpslocations?.[0]?.time || '';
      const bTime = b.gpslocations?.[0]?.time || '';
      return bTime.localeCompare(aTime);
    });

    const capped = sorted.slice(0, MAP_MAX_DRIVERS);
    return {
      drivers: capped,
      driversTruncated: sorted.length > MAP_MAX_DRIVERS,
    };
  }, [cappedMoves]);

  // Filter moves to only those belonging to capped drivers
  const driverIdSet = React.useMemo(
    () => new Set(drivers.map(d => d.id)),
    [drivers]
  );

  const moves = React.useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => cappedMoves.filter((m: any) => driverIdSet.has(m.driver_id)),
    [cappedMoves, driverIdSet]
  );

  // Moves for selected driver
  const driverMoves = React.useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => selectedDriverId ? moves.filter((m: any) => m.driver_id === selectedDriverId) : [],
    [moves, selectedDriverId]
  );

  // Color map
  const moveColorMap = React.useMemo(() => buildMoveColorMap(driverMoves), [driverMoves]);

  // Parse pings + pre-index by move_id
  const allPings: MapPing[] = pingsData?.driverlocations || [];

  const { pingsByMoveId, unassignedPings } = React.useMemo(() => {
    const byMoveId: Record<number, MapPing[]> = {};
    const unassigned: MapPing[] = [];

    allPings.forEach(ping => {
      if (ping.move_id) {
        if (!byMoveId[ping.move_id]) byMoveId[ping.move_id] = [];
        byMoveId[ping.move_id].push(ping);
      } else {
        unassigned.push(ping);
      }
    });

    return { pingsByMoveId: byMoveId, unassignedPings: unassigned };
  }, [allPings]);

  return {
    moves,
    drivers,
    moveColorMap,
    pingsByMoveId,
    unassignedPings,
    selectedDriverId,
    handleSelectDriver,
    highlightedMoveId,
    setHighlightedMoveId,
    driverMoves,
    loading: movesLoading,
    pingsLoading,
    movesTruncated,
    driversTruncated,
    totalMoveCount,
    refetch,
  };
}
